import React, { useEffect, useMemo, useState } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    message,
    Card,
    Typography,
    Tooltip,
    Select,
    Popconfirm,
    Space,
} from 'antd';
import {
    EditOutlined,
    PlusOutlined,
    InfoCircleOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import CommonLayout from '../../common/LayoutBlock';
import { useBreadcrumbs } from '../../context/BreadcrumbContext';
import { useAuthOrg } from '../../context/AuthOrgContext';
import { useNavigate } from 'react-router-dom';
import platformBudgetService, { PlatformConfig } from '../../apiServices/platformBudgetService';

const { Title } = Typography;

const budgetPercentFields = [
    {
        name: 'marginPercent',
        label: 'Margin Percent',
        requiredMessage: 'Please enter Margin Percent',
    },
    {
        name: 'dataCostPercent',
        label: 'Data Cost Percent',
        requiredMessage: 'Please enter Data Cost Percent',
    },
    {
        name: 'marketingCostPercent',
        label: 'Marketing Cost',
        requiredMessage: 'Please enter Marketing Cost',
    },
    {
        name: 'authenticationCostPercent',
        label: 'Authentication Cost',
        requiredMessage: 'Please enter Authentication Cost',
    },
    {
        name: 'utilityCostPercent',
        label: 'Utility Cost',
        requiredMessage: 'Please enter Utility Cost',
    },
] as const;

const getConfigValue = (config: PlatformConfig, keys: (keyof PlatformConfig)[]) => {
    for (const key of keys) {
        const value = config[key];
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value;
        }
    }

    return undefined;
};

const toPercentInputValue = (value?: number) =>
    typeof value === 'number' && Number.isFinite(value) ? Number((value * 100).toFixed(4)) : undefined;

const toDecimalValue = (value: unknown) => {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed / 100 : 0;
};

const formatPercentValue = (value?: number) =>
    typeof value === 'number' && Number.isFinite(value) ? `${(value * 100).toFixed(2)}%` : 'N/A';

const PlatformBudgetConfig: React.FC = () => {
    const [configs, setConfigs] = useState<PlatformConfig[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [deletingOrgId, setDeletingOrgId] = useState<string | null>(null);
    const [editingConfig, setEditingConfig] = useState<PlatformConfig | null>(null);

    const [form] = Form.useForm();
    const { setBreadcrumbs } = useBreadcrumbs();
    const { availableOrgs, currentOrg } = useAuthOrg();
    const navigate = useNavigate();

    useEffect(() => {
        setBreadcrumbs([
            {
                title: (
                    <div className="text-lg cursor-pointer" onClick={() => navigate(`/settings`)}>
                        Setting
                    </div>
                ),
            },
            {
                title: <div className="text-lg">Platform Budget Config</div>,
            },
        ]);
        fetchConfigs();
    }, [setBreadcrumbs, navigate]);

    const fetchConfigs = async () => {
        setLoading(true);
        try {
            const data = await platformBudgetService.fetchPlatformConfigs();
            setConfigs(Array.isArray(data) ? data : []);
        } catch (error: any) {
            message.error(error.message || 'Failed to fetch configurations');
        } finally {
            setLoading(false);
        }
    };

    const configuredOrgIds = useMemo(() => {
        return new Set(configs.map((config) => config.orgId));
    }, [configs]);

    const handleOpenModal = (config?: PlatformConfig) => {
        setEditingConfig(config || null);

        if (config) {
            form.setFieldsValue({
                orgId: config.orgId,
                marginPercent: toPercentInputValue(
                    getConfigValue(config, ['marginValue', 'platformBudgetPercent'])
                ),
                dataCostPercent: toPercentInputValue(
                    getConfigValue(config, ['dataCostValue', 'dataCostPercent'])
                ),
                marketingCostPercent: config.marketingCost,
                authenticationCostPercent: config.authenticationCost,
                utilityCostPercent: config.utilityCost,
                remarks: '',
            });
        } else {
            form.resetFields();
            if (currentOrg?.orgId) {
                form.setFieldsValue({ orgId: currentOrg.orgId });
            }
        }

        setIsModalVisible(true);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setEditingConfig(null);
        form.resetFields();
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);

            const {
                orgId,
                marginPercent,
                dataCostPercent,
                marketingCostPercent,
                authenticationCostPercent,
                utilityCostPercent,
                remarks,
            } = values;

            await platformBudgetService.updatePlatformBudgetConfig(orgId, {
                orgId,
                marginValue: toDecimalValue(marginPercent),
                dataCostValue: toDecimalValue(dataCostPercent),
                marketingCost: marketingCostPercent,
                authenticationCost: authenticationCostPercent,
                utilityCost: utilityCostPercent,
                remarks,
            });

            message.success('Configuration saved successfully');
            handleCloseModal();
            fetchConfigs();
        } catch (error: any) {
            if (error?.errorFields) return;
            message.error(error.message || 'Failed to save configuration');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (record: PlatformConfig) => {
        try {
            setDeletingOrgId(record.orgId);

            // Replace this with your actual delete API if the method name differs
            await platformBudgetService.deletePlatformConfig(record.orgId);

            message.success('Configuration deleted successfully');
            fetchConfigs();
        } catch (error: any) {
            message.error(error.message || 'Failed to delete configuration');
        } finally {
            setDeletingOrgId(null);
        }
    };

    const columns = [
        {
            title: 'Organization ID',
            dataIndex: 'orgId',
            key: 'orgId',
            render: (val: string) => {
                const org = availableOrgs.find((o) => o?.orgId === val);
                return org ? `${org.orgName}` : val;
            },
        },
        {
            title: 'Margin (%)',
            key: 'marginValue',
            render: (_: any, record: PlatformConfig) =>
                formatPercentValue(getConfigValue(record, ['marginValue', 'platformBudgetPercent'])),
        },
        {
            title: 'Data Cost (%)',
            key: 'dataCostPercent',
            render: (_: any, record: PlatformConfig) =>
                formatPercentValue(getConfigValue(record, ['dataCostValue', 'dataCostPercent'])),
        },
        {
            title: 'Marketing Cost (₹)',
            key: 'marketingCost',
            render: (_: any, record: PlatformConfig) =>
                record.marketingCost,
        },
        {
            title: 'Authentication Cost (₹)',
            key: 'authenticationCost',
            render: (_: any, record: PlatformConfig) =>
                record.authenticationCost,
        },
        {
            title: 'Utility Cost (₹)',
            key: 'utilityCost',
            render: (_: any, record: PlatformConfig) =>
                record.utilityCost,
        },
        {
            title: 'Last Updated',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            render: (val: string) => (val ? new Date(val).toLocaleString() : 'N/A'),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 180,
            render: (_: any, record: PlatformConfig) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleOpenModal(record)}
                    >
                    </Button>

                    <Popconfirm
                        title="Delete Configuration"
                        description="Are you sure you want to delete this configuration?"
                        onConfirm={() => handleDelete(record)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            loading={deletingOrgId === record.orgId}
                        >
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const orgOptions = availableOrgs.map((org: any) => {
        const isConfigured = configuredOrgIds.has(org.orgId);
        const isCurrentEditOrg = editingConfig?.orgId === org.orgId;

        return {
            label: `${org.orgName || 'Unnamed'}`,
            value: org.orgId,
            disabled: !isCurrentEditOrg && isConfigured,
        };
    });

    return (
        <CommonLayout>
            <div className="p-6 h-[90vh]">
                <Card
                    title={<Title level={4}>Platform Budget Configuration</Title>}
                    extra={
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => handleOpenModal()}
                        >
                            Create Config
                        </Button>
                    }
                    className="shadow-sm"
                >
                    <Table
                        columns={columns}
                        dataSource={configs}
                        rowKey={(record: any) => record.id || record.orgId}
                        loading={loading}
                        pagination={{ pageSize: 15 }}
                        scroll={{ x: 1100, y: '60vh' }}
                    />
                </Card>

                <Modal
                    title={editingConfig ? 'Edit Budget Configuration' : 'Create Budget Configuration'}
                    open={isModalVisible}
                    onOk={handleSubmit}
                    onCancel={handleCloseModal}
                    okText="Save"
                    cancelText="Cancel"
                    confirmLoading={submitting}
                    width={720}
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="orgId"
                            label="Organization ID"
                            rules={[{ required: true, message: 'Please select Organization ID' }]}
                        >
                            <Select
                                placeholder="Select Organization"
                                disabled={!!editingConfig}
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '')
                                        .toString()
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                                options={orgOptions}
                            />
                        </Form.Item>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                            {budgetPercentFields.map((field) => (
                                <Form.Item
                                    key={field.name}
                                    name={field.name}
                                    label={
                                        <span>
                                            {field.label}&nbsp;
                                            <Tooltip title="Value should be between 0 and 100">
                                                <InfoCircleOutlined />
                                            </Tooltip>
                                        </span>
                                    }
                                    rules={[{ required: true, message: field.requiredMessage }]}
                                >
                                    <InputNumber
                                        min={0}
                                        max={100}
                                        step={0.01}
                                        precision={4}
                                        placeholder="0.0000"
                                        className="w-full"
                                    />
                                </Form.Item>
                            ))}
                        </div>

                        <Form.Item
                            name="remarks"
                            label="Remarks"
                            rules={[{ required: true, message: 'Please enter remarks for this update' }]}
                        >
                            <Input.TextArea
                                placeholder="Enter remarks/reason for update"
                                rows={3}
                            />
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </CommonLayout>
    );
};

export default PlatformBudgetConfig;
